'use client'

import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { ShippingRateDetails, useShippingRate, useUpdateShippingRate, type UpdateShippingRateInput } from '@/lib/api/shippingRates'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { updateShippingRateSchema, type UpdateShippingRateFormData } from '@/components/form/shippingRate/validation'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { ShippingRateForm } from '@/components/form/shippingRate/shipping-rate-form'
import { ShippingDetailsSkeleton } from './components/shipping-details-skeleton'
import { ShippingDetailsNotFound } from './components/shipping-details-not-found'

const parseShippingRateToFormData = (shippingRate: any): UpdateShippingRateFormData => {
    return {
        shippingRateName: shippingRate.shippingRateName || '',
        description: shippingRate.description || '',
        icon: shippingRate.icon || undefined,
        amount: shippingRate.amount || {},
        isFree: shippingRate.isFree ?? false,
        isPickupPoint: shippingRate.isPickupPoint ?? false,
        highlighted: shippingRate.highlighted ?? false,
        shippingRateMinAmount: shippingRate.shippingRateMinAmount ?? null,
        shippingRateMaxAmount: shippingRate.shippingRateMaxAmount ?? null,
        shippingRateMinWeight: shippingRate.shippingRateMinWeight ?? null,
        shippingRateMaxWeight: shippingRate.shippingRateMaxWeight ?? null,
        countryCodes: shippingRate.countryCodes ?? [],
        pickupPointCarriers: shippingRate.pickupPointCarriers ?? null,
    }
}

const EditShippingRatePageInner = () => {
    const { shippingId: shippingRateId } = useParams()
    if (!shippingRateId) {
        throw new Error('Shipping rate ID is required')
    }

    const { data: shippingRateData, isLoading, error } = useShippingRate(shippingRateId)
    if (isLoading) {
        return <ShippingDetailsSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }
    if (!shippingRateData) {
        return <ShippingDetailsNotFound />
    }
    return <ShippingDetailsPage shippingRate={shippingRateData.shippingRate} />
}

interface ShippingDetailsPageProps {
    shippingRate: ShippingRateDetails
}

const ShippingDetailsPage = ({ shippingRate }: ShippingDetailsPageProps) => {
    const { t } = useTranslation('shippings')
    const { t: tCommon } = useTranslation('common')
    const { storeRoutes, navigate } = useStoreNavigate()
    const { toast } = useToast()

    const updateShippingRateMutation = useUpdateShippingRate()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const formData = shippingRate ? parseShippingRateToFormData(shippingRate) : null

    const methods = useForm<UpdateShippingRateFormData>({
        defaultValues: formData || {
            shippingRateName: '',
            description: '',
            icon: undefined,
            amount: {},
            isFree: false,
            isPickupPoint: false,
            highlighted: false,
            shippingRateMinAmount: null,
            shippingRateMaxAmount: null,
            shippingRateMinWeight: null,
            shippingRateMaxWeight: null,
            countryCodes: [],
            pickupPointCarriers: null,
        },
        resolver: zodResolver(updateShippingRateSchema),
        mode: 'onBlur',
    })

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting },
    } = methods

    React.useEffect(() => {
        if (shippingRate) {
            reset(parseShippingRateToFormData(shippingRate))
        }
    }, [shippingRate, reset])

    const handleUpdateShippingRate = async (data: UpdateShippingRateFormData) => {
        try {
            setFormError(null)

            const input: UpdateShippingRateInput = {
                id: shippingRate.id,
                data: {
                    shippingRateName: data.shippingRateName,
                    description: data.description || undefined,
                    icon: data.icon || undefined,
                    amount: data.amount,
                    isFree: data.isFree ?? false,
                    isPickupPoint: data.isPickupPoint ?? false,
                    highlighted: data.highlighted ?? false,
                    shippingRateMinAmount: data.shippingRateMinAmount || null,
                    shippingRateMaxAmount: data.shippingRateMaxAmount || null,
                    shippingRateMinWeight: data.shippingRateMinWeight || null,
                    shippingRateMaxWeight: data.shippingRateMaxWeight || null,
                    countryCodes: data.countryCodes ?? [],
                    pickupPointCarriers: data.pickupPointCarriers || null,
                },
            }

            await updateShippingRateMutation.mutateAsync(input)

            toast({
                title: tCommon('success'),
                description: t('updateSuccess'),
            })

            navigate(storeRoutes.SHIPPINGS())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: tCommon('error'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleUpdateShippingRate)}>
                        {/* Header */}
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-center gap-4">
                                <BackButton to={storeRoutes.SHIPPINGS()} />
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight">{t('editShippingRate')}</h1>
                                    <p className="text-muted-foreground mt-1 text-sm">{t('editDescription')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(storeRoutes.SHIPPINGS())}
                                    disabled={isSubmitting}
                                >
                                    {tCommon('cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={isSubmitting || updateShippingRateMutation.isPending}
                                >
                                    {isSubmitting || updateShippingRateMutation.isPending
                                        ? t('updating')
                                        : t('updateShippingRate')}
                                </Button>
                            </div>
                        </div>

                        {/* Error Display */}
                        {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

                        {/* Main Content */}
                        <div className="mt-6">
                            <ShippingRateForm />
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    )
}

export const EditShippingRatePage = withRequiredPermission(EditShippingRatePageInner, PERMISSIONS.STORES_MANAGE)

